import { getFilterEmptyLabel, getFilterLabel } from '@/shared/copy/filters';
import { r, s } from '@/theme/tokens';
import { Stack } from '@/ui/layout/Stack';
import { Box, Popover, useTheme } from '@/ui/primitives';
import { resolveChartPlotAreaBackgroundColor } from '@/ui/primitives/chartPlotAreaBackgroundColor';
import { TagsFilterField } from './TagsFilterField';
import type { PopoverProps } from '@/ui/primitives';
import type { ReactElement } from 'react';

const popoverFontSize = 15;

type DropdownFilterOption = {
  value: string;
  label: string;
  count?: number;
};

type DropdownFiltersPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  selectedStatuses: string[];
  onStatusesChange: (next: string[]) => void;
  statusOptions: DropdownFilterOption[];
  statusLabel?: string;
  selectedEntities?: string[];
  onEntitiesChange?: (next: string[]) => void;
  entityOptions?: DropdownFilterOption[];
  entityLabel?: string;
  selectedItems?: string[];
  onItemsChange?: (next: string[]) => void;
  itemOptions?: DropdownFilterOption[];
  itemLabel?: string;
  selectedSuites?: string[];
  onSuitesChange?: (next: string[]) => void;
  suiteOptions?: DropdownFilterOption[];
  suiteLabel?: string;
  selectedTags: string[];
  onTagsChange: (next: string[]) => void;
  tagOptions: DropdownFilterOption[];
  selectedProjects?: string[];
  onProjectsChange?: (next: string[]) => void;
  projectOptions?: DropdownFilterOption[];
  projectLabel?: string;
  anchorOrigin?: PopoverProps['anchorOrigin'];
  transformOrigin?: PopoverProps['transformOrigin'];
};

export const DropdownFiltersPopover = ({
  open,
  anchorEl,
  onClose,
  selectedStatuses,
  onStatusesChange,
  statusOptions,
  statusLabel = 'Latest Status',
  selectedEntities,
  onEntitiesChange,
  entityOptions,
  entityLabel = 'Item',
  selectedItems,
  onItemsChange,
  itemOptions,
  itemLabel = 'Item',
  selectedSuites,
  onSuitesChange,
  suiteOptions,
  suiteLabel = 'Suite',
  selectedTags,
  onTagsChange,
  tagOptions,
  selectedProjects,
  onProjectsChange,
  projectOptions,
  projectLabel = getFilterLabel('project'),
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  transformOrigin = { vertical: 'top', horizontal: 'right' },
}: DropdownFiltersPopoverProps): ReactElement => {
  const theme = useTheme();
  const toolbarFieldBackgroundColor = resolveChartPlotAreaBackgroundColor(theme);
  const hasSuiteFilter =
    selectedSuites !== undefined && onSuitesChange !== undefined && suiteOptions !== undefined;
  const hasEntityFilter =
    selectedEntities !== undefined && onEntitiesChange !== undefined && entityOptions !== undefined;
  const hasItemFilter =
    selectedItems !== undefined && onItemsChange !== undefined && itemOptions !== undefined;
  const hasProjectFilter =
    selectedProjects !== undefined &&
    onProjectsChange !== undefined &&
    projectOptions !== undefined;
  const showStatusFilter = statusOptions.length > 0;
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      sx={{
        '& .MuiPopover-paper': {
          borderRadius: `${r('md')} !important`,
          overflow: 'visible !important',
        },
      }}
      paperSx={{
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: `${r('md')} !important`,
        overflow: 'visible !important',
        boxShadow: 'none',
        minWidth: 0,
        width: 'fit-content',
      }}
    >
      <Stack gap="none" sx={{ borderRadius: `${r('md')} !important`, overflow: 'visible' }}>
        <Box
          sx={{
            pl: s('lg'),
            pr: s('lg'),
            py: s('lg'),
            width: 'fit-content',
            minWidth: 'fit-content',
          }}
        >
          <Stack gap="md" sx={{ justifyContent: 'flex-start', alignItems: 'flex-end' }}>
            {hasSuiteFilter ? (
              <TagsFilterField
                label={suiteLabel}
                fontSizePx={popoverFontSize}
                labelPlacement="inline"
                labelControlGap={s('md')}
                inputAriaLabel={`Filter by ${suiteLabel.toLowerCase()}`}
                value={selectedSuites}
                options={suiteOptions}
                emptyLabel={getFilterEmptyLabel('all')}
                controlWidthPx={180}
                inputBackgroundColor={toolbarFieldBackgroundColor}
                inputLeftPadding={s('xs')}
                onChange={onSuitesChange}
              />
            ) : null}
            {hasEntityFilter ? (
              <TagsFilterField
                label={entityLabel}
                fontSizePx={popoverFontSize}
                labelPlacement="inline"
                labelControlGap={s('md')}
                inputAriaLabel={`Filter by ${entityLabel.toLowerCase()}`}
                value={selectedEntities}
                options={entityOptions}
                emptyLabel={getFilterEmptyLabel('all')}
                controlWidthPx={180}
                inputBackgroundColor={toolbarFieldBackgroundColor}
                inputLeftPadding={s('xs')}
                onChange={onEntitiesChange}
              />
            ) : null}
            {hasItemFilter ? (
              <TagsFilterField
                label={itemLabel}
                fontSizePx={popoverFontSize}
                labelPlacement="inline"
                labelControlGap={s('md')}
                inputAriaLabel={`Filter by ${itemLabel.toLowerCase()}`}
                value={selectedItems}
                options={itemOptions}
                emptyLabel={getFilterEmptyLabel('all')}
                controlWidthPx={180}
                inputBackgroundColor={toolbarFieldBackgroundColor}
                inputLeftPadding={s('xs')}
                onChange={onItemsChange}
              />
            ) : null}
            {showStatusFilter ? (
              <TagsFilterField
                label={statusLabel}
                fontSizePx={popoverFontSize}
                labelPlacement="inline"
                labelControlGap={s('md')}
                inputAriaLabel={`Filter by ${statusLabel.toLowerCase()}`}
                value={selectedStatuses}
                options={statusOptions}
                onChange={onStatusesChange}
                emptyLabel={getFilterEmptyLabel('all')}
                controlWidthPx={180}
                inputBackgroundColor={toolbarFieldBackgroundColor}
                inputLeftPadding={s('xs')}
              />
            ) : null}
            <TagsFilterField
              label={getFilterLabel('tags')}
              fontSizePx={popoverFontSize}
              labelPlacement="inline"
              labelControlGap={s('md')}
              inputAriaLabel="Filter by tags"
              value={selectedTags}
              options={tagOptions}
              onChange={onTagsChange}
              emptyLabel={
                tagOptions.length > 0 ? getFilterEmptyLabel('all') : getFilterEmptyLabel('tags')
              }
              controlWidthPx={180}
              inputBackgroundColor={toolbarFieldBackgroundColor}
              inputLeftPadding={s('xs')}
            />
            {hasProjectFilter ? (
              <TagsFilterField
                label={projectLabel}
                fontSizePx={popoverFontSize}
                labelPlacement="inline"
                labelControlGap={s('md')}
                inputAriaLabel={`Filter by ${projectLabel.toLowerCase()}`}
                value={selectedProjects}
                options={projectOptions}
                onChange={onProjectsChange}
                emptyLabel={getFilterEmptyLabel('all')}
                controlWidthPx={180}
                inputBackgroundColor={toolbarFieldBackgroundColor}
                inputLeftPadding={s('xs')}
              />
            ) : null}
          </Stack>
        </Box>
      </Stack>
    </Popover>
  );
};

export type { DropdownFilterOption, DropdownFiltersPopoverProps };
